import React, { useEffect } from 'react';
import Logo from './assets/logo-bolao-top-cropped.png';

const OrganizarBolaoAmigos = ({ onBack, onLoginClick }) => {
  useEffect(() => {
    // SEO setup
    const title = "Como organizar um bolão entre amigos (sem dor de cabeça) | Bolões TOP";
    const description = "Aprenda como organizar um bolão entre amigos de forma prática, justa e sem complicação. Guia passo a passo para criar seu bolão online grátis.";
    const url = "https://boloestop.com.br/criar-organizar-bolao-gratis";

    document.title = title;

    // Update Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Update Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Update OG Tags
    const updateMetaTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', url);
    updateMetaTag('og:type', 'article');

    // Add JSON-LD Structured Data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": title,
      "description": description,
      "author": {
        "@type": "Organization",
        "name": "Bolões TOP"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Bolões TOP",
        "logo": {
          "@type": "ImageObject",
          "url": "https://boloestop.com.br/logo512.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url
      }
    };

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Como convidar amigos para o meu bolão?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Com o Bolões TOP, você pode convidar amigos facilmente enviando o link do bolão diretamente via WhatsApp ou E-mail."
          }
        },
        {
          "@type": "Question",
          "name": "É possível organizar bolão de Fórmula 1 entre amigos?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sim, além do futebol, o Bolões TOP oferece suporte completo para bolões de F1 com ranking e palpites automáticos."
          }
        }
      ]
    };

    const scriptSchema = document.createElement('script');
    scriptSchema.type = "application/ld+json";
    scriptSchema.id = "json-ld-article-organizar";
    scriptSchema.text = JSON.stringify(schemaData);
    document.head.appendChild(scriptSchema);

    const scriptFaq = document.createElement('script');
    scriptFaq.type = "application/ld+json";
    scriptFaq.id = "json-ld-faq-organizar";
    scriptFaq.text = JSON.stringify(faqData);
    document.head.appendChild(scriptFaq);

    window.scrollTo(0, 0);

    return () => {
      const s1 = document.getElementById('json-ld-article-organizar');
      const s2 = document.getElementById('json-ld-faq-organizar');
      if (s1) s1.remove();
      if (s2) s2.remove();
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col min-h-screen text-slate-200 font-sans bg-[#0f172a]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-slate-700 shadow-lg bg-slate-900/70 backdrop-blur-md" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex-shrink-0 flex items-center cursor-pointer hover:opacity-80 transition-opacity" onClick={onBack}>
              <img src={Logo} alt="Bolões Top" className="h-14 w-auto" />
            </div>
            <div>
              <button
                onClick={onLoginClick}
                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white font-bold py-1.5 px-4 sm:py-2 sm:px-6 text-xs sm:text-base rounded-full shadow-lg transform hover:scale-105 transition-all duration-200 whitespace-nowrap"
              >
                <span className="hidden sm:inline">CRIAR BOLÃO GRÁTIS</span>
                <span className="sm:hidden">CRIAR BOLÃO</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative py-16 md:py-24 overflow-hidden border-b border-slate-800">
           {/* Decorative Background */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 -right-24 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-8">
              👥 <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400">Como organizar um bolão entre amigos</span> (sem dor de cabeça)
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto italic">
              "Organizar um bolão entre amigos parece simples — até começar a dar problema."
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <section className="py-16 bg-slate-900/50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-slate-800/80 p-8 rounded-3xl border border-slate-700 shadow-xl mb-12">
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Mensagem perdida, gente que esquece de palpitar, erro na pontuação… tudo isso pode transformar a diversão em dor de cabeça.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed font-semibold text-green-400">
                A boa notícia é que dá pra evitar tudo isso com um processo simples. Neste guia, você vai aprender como organizar um bolão de forma prática, justa e sem complicação.
              </p>
            </div>

            <div className="space-y-16">
              {/* Por que organizar bem */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                  <span className="text-3xl">🎯</span> Por que organizar bem um bolão é tão importante?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                   <div className="bg-red-500/5 border border-red-500/20 p-8 rounded-3xl">
                      <h3 className="text-xl font-bold text-white mb-4">Um bolão mal organizado causa:</h3>
                      <ul className="space-y-2 text-slate-400">
                        <li className="flex items-center gap-2">❌ Confusão nas regras</li>
                        <li className="flex items-center gap-2">❌ Erros de pontuação</li>
                        <li className="flex items-center gap-2">❌ Desmotivação dos participantes</li>
                      </ul>
                      <p className="mt-4 text-xs text-slate-500 italic">👉 E isso faz as pessoas abandonarem o jogo.</p>
                   </div>
                   <div className="bg-green-500/5 border border-green-500/20 p-8 rounded-3xl">
                      <h3 className="text-xl font-bold text-white mb-4">Um bolão bem feito:</h3>
                      <ul className="space-y-2 text-slate-400">
                        <li className="flex items-center gap-2">✅ Engaja todo mundo</li>
                        <li className="flex items-center gap-2">✅ Mantém a disputa até o final</li>
                        <li className="flex items-center gap-2">✅ Vira tradição no grupo</li>
                      </ul>
                   </div>
                </div>
              </div>

              {/* Passo a passo */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 flex items-center gap-3">
                  <span className="text-3xl">📋</span> Passo a passo para organizar um bolão perfeito
                </h2>
                
                <div className="space-y-12">
                   <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-xl font-black text-slate-900 shadow-[0_0_15px_rgba(34,197,94,0.4)]">1</div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-white mb-3">Defina regras claras desde o início</h3>
                        <p className="text-slate-400 mb-4">Antes de convidar qualquer pessoa, defina pontuação, prazos e critérios de desempate.</p>
                        <button onClick={() => window.location.href='/regras-bolao-online-gratis'} className="text-teal-400 hover:underline text-sm font-bold">
                           ➡️ Veja nosso guia completo de regras aqui
                        </button>
                      </div>
                   </div>

                   <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex-shrink-0 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-xl font-black text-slate-900 shadow-[0_0_15px_rgba(250,204,21,0.4)]">2</div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-white mb-3">Escolha a forma de organização</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                           <div className="bg-slate-800 p-5 rounded-2xl border border-slate-700 opacity-60">
                              <h4 className="text-red-400 font-bold mb-2 flex items-center gap-2">❌ Planilha + WhatsApp</h4>
                              <p className="text-xs text-slate-500">Bagunça rápido, gera erros e é difícil de controlar.</p>
                           </div>
                           <div className="bg-green-500/10 p-5 rounded-2xl border-2 border-green-500 shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                              <h4 className="text-green-400 font-bold mb-2 flex items-center gap-2">✅ Plataforma Online</h4>
                              <p className="text-xs text-green-300/70">Tudo automatizado, ranking em tempo real e sem esforço manual.</p>
                           </div>
                        </div>
                      </div>
                   </div>
                </div>
              </div>

              {/* Call to Action Card */}
              <div className="bg-gradient-to-br from-green-600 to-slate-900 p-8 md:p-12 rounded-[2rem] shadow-2xl border border-green-500/30 text-center">
                <h3 className="text-2xl md:text-4xl font-black text-white mb-6">
                  🚀 Crie e organize seu bolão em <span className="text-yellow-400">minutos</span>
                </h3>
                <p className="text-lg text-slate-200 mb-10 max-w-xl mx-auto">
                   O Bolões TOP resolve os principais problemas automaticamente para você.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-10 max-w-2xl mx-auto">
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Ranking em tempo real
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Cálculo automático de pontos
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Notificações por email
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Convite fácil via WhatsApp
                  </div>
                </div>
                <button
                  onClick={onLoginClick}
                  className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-black py-4 px-12 rounded-2xl text-xl shadow-xl transform hover:scale-105 transition-all"
                >
                  CRIAR BOLÃO GRÁTIS AGORA
                </button>
                <p className="text-green-200/60 mt-6 text-sm">Sem limite de participantes | Acesse: boloestop.com.br</p>
              </div>

              {/* Erros comuns */}
              <div className="bg-slate-800/40 p-8 rounded-3xl border border-slate-700/50">
                 <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <span className="text-2xl">⚠️</span> Erros comuns ao organizar bolão entre amigos
                 </h3>
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      "Regras mudando no meio do campeonato",
                      "Falta de prazo definido para palpites",
                      "Controle manual confuso e demorado",
                      "Demora para atualizar os resultados"
                    ].map((err, i) => (
                      <div key={i} className="flex gap-3 bg-red-500/10 p-4 rounded-xl border border-red-500/20">
                        <span className="text-red-500">❌</span>
                        <span className="text-slate-300 text-sm">{err}</span>
                      </div>
                    ))}
                 </div>
                 <p className="text-slate-500 mt-6 text-sm text-center italic">👉 Esses são os principais motivos de abandono do bolão.</p>
              </div>

              {/* Dicas e Grandes Eventos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                       <span className="text-2xl">💡</span> Dicas para mais diversão
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                       <li className="flex gap-2"><span className="text-yellow-400">★</span> Crie um grupo só para interação</li>
                       <li className="flex gap-2"><span className="text-yellow-400">★</span> Defina uma pequena premiação</li>
                       <li className="flex gap-2"><span className="text-yellow-400">★</span> Incentive rivalidade saudável</li>
                       <li className="flex gap-2"><span className="text-yellow-400">★</span> Acompanhe os rankings juntos</li>
                    </ul>
                 </div>
                 <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                       <span className="text-2xl">🌍</span> Aproveite grandes eventos
                    </h3>
                    <p className="text-slate-400 text-sm mb-6">Eventos como a Copa do Mundo FIFA 2026 são perfeitos para bolões.</p>
                    <button onClick={() => window.location.href='/bolao-copa-2026'} className="text-teal-400 hover:underline font-bold text-sm">
                       ➡️ Guia completo do Bolão da Copa 2026
                    </button>
                 </div>
              </div>

              {/* Fórmula 1 */}
              <div className="bg-slate-800/40 p-8 rounded-3xl border border-slate-700/50 text-center">
                 <h2 className="text-2xl font-bold text-white mb-4 flex items-center justify-center gap-2">
                    <span className="text-2xl">🏎️</span> Bolão de Fórmula 1 também!
                 </h2>
                 <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                    Além do futebol, você pode organizar bolões de F1 com seus amigos. O Bolões TOP já tem tudo pronto: palpites de corrida e ranking automático.
                 </p>
                 <span className="bg-slate-700 px-4 py-2 rounded-full text-blue-400 border border-blue-400/30 uppercase text-xs font-bold">Ideal para grupos que curtem automobilismo</span>
              </div>

              {/* Conclusão */}
              <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700 text-center">
                <h2 className="text-2xl font-bold text-white mb-4">🎯 Conclusão</h2>
                <p className="text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                   Organizar um bolão entre amigos não precisa ser complicado. Com as regras certas e a ferramenta correta, você transforma tudo em uma experiência divertida e sem dor de cabeça.
                </p>
                <button
                  onClick={onLoginClick}
                  className="bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-12 rounded-xl shadow-lg transition-all transform hover:scale-105"
                >
                  COMEÇAR AGORA
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 pt-12 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center italic text-slate-500 text-sm">
          <p>&copy; 2026 Bolões Top. O guia definitivo para seu bolão.</p>
          <button onClick={scrollToTop} className="mt-4 text-teal-500 hover:underline">Voltar ao topo</button>
        </div>
      </footer>
    </div>
  );
};

export default OrganizarBolaoAmigos;
